import { Gift, Trophy } from "lucide-react";
import { Card, CardContent } from "../ui/card";
import { Badge } from "../ui/badge";
import { imageSrc } from "../../lib/api";
import { nok } from "../../lib/basar";
import { useLang } from "../../context/LanguageContext";

// Varm, nøytral plassholdar for premiar utan bilete
export const PRIZE_PLACEHOLDER = "https://images.unsplash.com/photo-1513201099705-a9746e1e201f?auto=format&fit=crop&w=1200&q=80";

export const PrizeImage = ({ prize, className = "" }) => {
  if (!prize?.image_url) {
    return (
      <div className={`h-full w-full flex items-center justify-center bg-[hsl(var(--warning))]/10 text-[hsl(var(--warning))] ${className}`} data-testid={`basar-prize-image-${prize?.id || "preview"}`}>
        <Gift className="w-10 h-10" aria-hidden="true" />
      </div>
    );
  }
  return (
    <img
      src={imageSrc(prize.image_url)}
      alt={prize.name || ""}
      className={`h-full w-full object-cover ${className}`}
      loading="lazy"
      onError={(e) => {
        e.currentTarget.src = PRIZE_PLACEHOLDER;
      }}
      data-testid={`basar-prize-image-${prize.id || "preview"}`}
    />
  );
};

/** Premiekort: bilete 4:3, verdi-badge (amber) og «× N» når fleire eksemplar. `winners` (valfri) = [{number, name}] etter publisert trekning. */
export const PrizeCard = ({ prize, index, winners, compact = false }) => {
  const { t } = useLang();
  const count = Number(prize.count || 1);
  return (
    <Card className="overflow-hidden h-full border-border bg-card flex flex-col" data-testid="basar-prize-card" data-prize-id={prize.id}>
      <div className={`relative ${compact ? "aspect-[16/9]" : "aspect-[4/3]"} overflow-hidden bg-muted`}>
        <PrizeImage prize={prize} />
        <div className="absolute top-2 right-2 flex flex-col items-end gap-1">
          {Number(prize.value) > 0 && (
            <Badge className="shadow-sm bg-[hsl(var(--warning))] text-[hsl(var(--warning-foreground))] hover:bg-[hsl(var(--warning))] tabular-nums" data-testid="basar-prize-value-badge">{t("Verdi {amount}", { amount: nok(prize.value) })}</Badge>
          )}
          {count > 1 && <Badge variant="secondary" className="shadow-sm tabular-nums" data-testid="basar-prize-copies-badge">× {count}</Badge>}
        </div>
      </div>
      <CardContent className={`${compact ? "p-3" : "p-4"} flex flex-col gap-1.5 flex-1`}>
        {index != null && <p className="text-xs text-muted-foreground">{t("Premie {n}", { n: index + 1 })}</p>}
        <h3 className="font-heading font-semibold leading-snug line-clamp-2" data-testid="basar-prize-name">{prize.name}</h3>
        {prize.description && !compact && <p className="text-sm text-muted-foreground line-clamp-3 whitespace-pre-line">{prize.description}</p>}
        {winners && winners.length > 0 && (
          <ul className="mt-2 space-y-1 border-t border-border pt-2" data-testid="basar-prize-winners">
            {winners.map((w, i) => (
              <li key={`${w.number}-${i}`} className="flex items-center gap-2 text-sm" data-testid="basar-prize-winner-row">
                <Trophy className="w-4 h-4 text-[hsl(var(--warning))] shrink-0" aria-hidden="true" />
                <span className="font-semibold tabular-nums">{t("Lodd {n}", { n: w.number })}</span>
                {w.name && <span className="text-muted-foreground truncate">– {w.name}</span>}
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
};

/** Galleri over premiane. `winnersByPrize` = { [prize_id]: [{number, name}] } når trekninga er publisert. */
export const PrizeGallery = ({ prizes, winnersByPrize, compact = false, testId = "basar-prize-gallery" }) => {
  if (!prizes?.length) return null;
  return (
    <div className={`grid gap-4 ${compact ? "grid-cols-2 lg:grid-cols-3" : "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 lg:gap-5"}`} data-testid={testId}>
      {prizes.map((p, i) => (
        <PrizeCard key={p.id || i} prize={p} index={i} winners={winnersByPrize?.[p.id]} compact={compact} />
      ))}
    </div>
  );
};
